import { useEffect, useState } from "react";
import { api, fmtDateTime } from "@/lib/api";
import { PageHeader } from "./AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, CheckCircle2, XCircle, Clock, FileCheck } from "lucide-react";

const STATUS_OPTS = [
  { v: "open", l: "Mở", c: "bg-stone-200 text-stone-800" },
  { v: "in_progress", l: "Đang làm", c: "bg-blue-100 text-blue-900" },
  { v: "submitted", l: "Chờ duyệt", c: "bg-amber-100 text-amber-900" },
  { v: "approved", l: "Đã duyệt", c: "bg-emerald-100 text-emerald-900" },
  { v: "rejected", l: "Trả lại", c: "bg-red-100 text-red-900" },
];

export default function Tasks() {
  const [data, setData] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [sops, setSops] = useState([]);
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [form, setForm] = useState({ title: "", description: "", priority: "normal" });

  const load = () => {
    const url = filter ? `/tasks?status=${filter}` : "/tasks";
    api.get(url).then((r) => setData(r.data));
  };
  useEffect(() => {
    load();
    api.get("/employees").then((r) => setEmployees(r.data));
    api.get("/sops").then((r) => setSops(r.data));
    api.get("/branches").then((r) => setBranches(r.data));
    // eslint-disable-next-line
  }, [filter]);

  const create = async () => {
    try {
      await api.post("/tasks", {
        ...form,
        due_at: form.due_at ? new Date(form.due_at).toISOString() : null,
        sop_id: form.sop_id || null, assignee_id: form.assignee_id || null, branch_id: form.branch_id || null,
      });
      toast.success("Đã giao việc"); setOpen(false); setForm({ title: "", description: "", priority: "normal" }); load();
    } catch (e) { toast.error("Lỗi"); }
  };

  const transition = async (id, action) => {
    try { await api.post(`/tasks/${id}/transition`, { action }); load(); toast.success(`Đã ${action}`); }
    catch { toast.error("Thất bại"); }
  };

  return (
    <div data-testid="page-tasks">
      <PageHeader
        kicker="HR-Ops"
        title="Task & SOP"
        breadcrumb={["HR-Ops", "Task Manager"]}
        actions={
          <Button onClick={() => setOpen(true)} className="bg-emerald-800 hover:bg-emerald-900 rounded-sm" data-testid="btn-create-task">
            <Plus className="w-4 h-4 mr-1.5" /> Giao việc
          </Button>
        }
      />
      <div className="p-6 lg:p-8 space-y-4">
        <div className="flex gap-2 flex-wrap">
          <Chip active={!filter} onClick={() => setFilter("")}>Tất cả</Chip>
          {STATUS_OPTS.map((s) => (
            <Chip key={s.v} active={filter === s.v} onClick={() => setFilter(s.v)} cls={s.c}>{s.l}</Chip>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {data.map((t) => {
            const st = STATUS_OPTS.find((s) => s.v === t.status);
            return (
              <div key={t.id} className="bg-white border border-stone-200 rounded-sm p-4 hover:shadow-sm transition-shadow" data-testid={`task-${t.id}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold mb-1">{t.branch_name || "—"}</div>
                    <h3 className="font-semibold text-stone-900">{t.title}</h3>
                  </div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-semibold ${st?.c}`}>{st?.l}</span>
                </div>
                {t.description && <p className="text-xs text-stone-600 mb-2">{t.description}</p>}
                {t.sop_name && (
                  <div className="text-xs text-emerald-800 mb-2 flex items-center gap-1">
                    <FileCheck className="w-3 h-3" /> SOP: {t.sop_name}
                  </div>
                )}
                <div className="flex items-center justify-between text-xs text-stone-500 mb-2 pt-2 border-t border-stone-100">
                  <span>👤 {t.assignee_name || "—"}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{fmtDateTime(t.due_at)}</span>
                </div>
                {t.priority === "high" && <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-900 font-bold rounded-sm">⚡ ƯU TIÊN</span>}

                <div className="flex gap-1.5 mt-3 pt-3 border-t border-stone-100">
                  {t.status === "open" && (
                    <button onClick={() => transition(t.id, "start")} className="text-xs px-2 py-1 bg-blue-50 text-blue-900 rounded-sm hover:bg-blue-100" data-testid={`btn-start-${t.id}`}>Bắt đầu</button>
                  )}
                  {t.status === "in_progress" && (
                    <button onClick={() => transition(t.id, "submit")} className="text-xs px-2 py-1 bg-amber-50 text-amber-900 rounded-sm hover:bg-amber-100" data-testid={`btn-submit-${t.id}`}>Gửi duyệt</button>
                  )}
                  {t.status === "submitted" && (
                    <>
                      <button onClick={() => transition(t.id, "approve")} className="text-xs px-2 py-1 bg-emerald-100 text-emerald-900 rounded-sm hover:bg-emerald-200 font-semibold flex items-center gap-1" data-testid={`btn-approve-${t.id}`}>
                        <CheckCircle2 className="w-3 h-3" />Duyệt
                      </button>
                      <button onClick={() => transition(t.id, "reject")} className="text-xs px-2 py-1 bg-red-50 text-red-900 rounded-sm hover:bg-red-100 flex items-center gap-1" data-testid={`btn-reject-${t.id}`}>
                        <XCircle className="w-3 h-3" />Trả lại
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {data.length === 0 && <div className="text-center text-stone-400 py-10">Không có công việc</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Giao việc mới</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Tiêu đề *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="h-9 rounded-sm mt-1" data-testid="f-task-title" />
            </div>
            <div>
              <Label className="text-xs">Mô tả</Label>
              <Textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm mt-1" rows={3} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Người nhận</Label>
                <Select value={form.assignee_id || undefined} onValueChange={(v) => setForm({ ...form, assignee_id: v })}>
                  <SelectTrigger className="h-9 rounded-sm mt-1"><SelectValue placeholder="Chọn nhân viên" /></SelectTrigger>
                  <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name} ({e.code})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">SOP</Label>
                <Select value={form.sop_id || undefined} onValueChange={(v) => setForm({ ...form, sop_id: v })}>
                  <SelectTrigger className="h-9 rounded-sm mt-1"><SelectValue placeholder="Không bắt buộc" /></SelectTrigger>
                  <SelectContent>{sops.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Chi nhánh</Label>
                <Select value={form.branch_id || undefined} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                  <SelectTrigger className="h-9 rounded-sm mt-1"><SelectValue placeholder="Chọn" /></SelectTrigger>
                  <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Ưu tiên</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="h-9 rounded-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Thấp</SelectItem>
                    <SelectItem value="normal">Bình thường</SelectItem>
                    <SelectItem value="high">Cao</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Hạn chót</Label>
              <Input type="datetime-local" value={form.due_at || ""} onChange={(e) => setForm({ ...form, due_at: e.target.value })} className="h-9 rounded-sm mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Hủy</Button>
            <Button onClick={create} className="bg-emerald-800 hover:bg-emerald-900 rounded-sm" data-testid="btn-save-task">Giao</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Chip({ children, active, onClick, cls = "bg-stone-100 text-stone-700" }) {
  return (
    <button
      onClick={onClick}
      className={`text-xs px-3 py-1.5 rounded-sm font-semibold transition-colors ${active ? "bg-emerald-900 text-white" : cls + " hover:bg-stone-200"}`}
    >{children}</button>
  );
}
